import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { WHATS_NEW } from "@/lib/whatsNew";

/**
 * WhatsNewDialog — self-contained release-notes notice.
 *
 * Auto-opens on mount when:
 *   • a logged-in user exists, AND
 *   • the user has not yet dismissed the current `WHATS_NEW.version`
 *
 * Dismissal is persisted to `localStorage` namespaced by user id, so the
 * notice never shows the same version twice for the same account on the
 * same device. localStorage failures are swallowed silently.
 *
 * Theming follows the same `var(--bg-canvas)` / `var(--border-subtle)`
 * tokens every other dialog uses, so it adapts to light/dark automatically.
 *
 * No props, no parent state — just mount it once inside any authenticated
 * route. To announce a new release, edit `/lib/whatsNew.js` only.
 */

const storageKey = (uid) => `mileageiq:whatsNew:${uid || "anon"}`;

function readSeenVersion(uid) {
  try {
    return localStorage.getItem(storageKey(uid));
  } catch {
    return null;
  }
}

function writeSeenVersion(uid, version) {
  try {
    localStorage.setItem(storageKey(uid), version);
  } catch {
    /* noop — quota / private mode */
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function WhatsNewDialog() {
  const { user, loading } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    const seen = readSeenVersion(user.user_id || user.id || user.email);
    if (seen !== WHATS_NEW.version) {
      setOpen(true);
    }
  }, [loading, user]);

  const handleDismiss = () => {
    const uid = user?.user_id || user?.id || user?.email;
    writeSeenVersion(uid, WHATS_NEW.version);
    setOpen(false);
  };

  if (!WHATS_NEW?.version || !Array.isArray(WHATS_NEW.items) || WHATS_NEW.items.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleDismiss();
        else setOpen(true);
      }}
    >
      <DialogContent
        className="bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-[var(--text-primary)] sm:max-w-[480px]"
        data-testid="whats-new-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center justify-center w-7 h-7 bg-[#FFB340]/12 border border-[#FFB340]/30"
              style={{ borderRadius: 999 }}
            >
              <Sparkles className="w-3.5 h-3.5 text-[#FFB340]" strokeWidth={1.75} />
            </span>
            <DialogTitle
              className="font-display text-xl font-light tracking-tight"
              data-testid="whats-new-title"
            >
              {WHATS_NEW.title}
            </DialogTitle>
          </div>
          <DialogDescription
            className="text-[var(--text-muted)] text-xs tracking-wider uppercase"
            data-testid="whats-new-date"
          >
            Released {formatDate(WHATS_NEW.date)} · v{WHATS_NEW.version}
          </DialogDescription>
        </DialogHeader>

        <ul
          className="space-y-2 text-sm text-[var(--text-secondary)] leading-relaxed"
          data-testid="whats-new-items"
        >
          {WHATS_NEW.items.map((item, i) => (
            <li
              key={i}
              className="flex gap-2 items-start"
              data-testid={`whats-new-item-${i}`}
            >
              <span
                className="mt-1.5 inline-block w-1 h-1 shrink-0 bg-[#E82127]"
                aria-hidden="true"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button
            type="button"
            onClick={handleDismiss}
            className="bg-[var(--bg-inverse)] hover:bg-[var(--bg-inverse-hover)] text-[var(--text-inverse)]"
            data-testid="whats-new-dismiss-btn"
          >
            Dismiss
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
